import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {MutationObserver | null} */
  #priceObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Bundle/quantity apps update the visible sale and compare-at prices in
    // place, without triggering Shopify's variant event. Keep the discount
    // badge derived from the prices currently shown to the customer.
    this.#priceObserver = new MutationObserver(this.#syncDiscountBadge);
    this.#priceObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    this.#syncDiscountBadge();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#priceObserver?.disconnect();
    this.#priceObserver = null;

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** Recalculates the discount badge from the rendered prices. */
  #syncDiscountBadge = () => {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const salePrice = priceContainer?.querySelector('.price-item--sale.price, .price__sale .price');
    const comparePrice = priceContainer?.querySelector('.compare-at-price');
    const discountBadge = priceContainer?.querySelector('.price-discount-badge');

    if (!salePrice || !comparePrice || !discountBadge) return;

    const parsePrice = (element) => Number(element.textContent.replace(/[^\d.]/g, ''));
    const saleAmount = parsePrice(salePrice);
    const compareAmount = parsePrice(comparePrice);

    if (!saleAmount || !compareAmount || compareAmount <= saleAmount) return;

    const discount = Math.round(((compareAmount - saleAmount) * 100) / compareAmount);
    const nextText = `${discount}% OFF`;

    if (discountBadge.textContent.trim() !== nextText) {
      discountBadge.textContent = nextText;
    }
  };

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
